import { Injectable, InjectionToken, inject } from '@angular/core';
import {
    Client,
    Config,
    OptimizelyDecision,
    OptimizelyUserContext,
    createInstance,
} from '@optimizely/optimizely-sdk';
import { IqUserService } from '../iq-user/iq-user.service';
import { IIqUser } from '../models';
import { uniqueId } from 'lodash';

export class IqOptimizelyConfig {
    dataFileUrl: string;
    fetchPollingSeconds: number;
    logLevel: number;
    sdkKey: string;
}

export const OPTIMIZELY_CONFIG = new InjectionToken<IqOptimizelyConfig>(
    'OPTIMIZELY_CONFIG',
);

export type IqOptimizelyVariable = string | object | boolean | null;

export interface IIqFeatureFlagService {
    getVariable(flag: string, variable: string): Promise<unknown>;
}

@Injectable({
    providedIn: 'root',
})
export class IqFeatureFlagService implements IIqFeatureFlagService {
    private _client: Client;
    private _config: IqOptimizelyConfig = inject(OPTIMIZELY_CONFIG);
    private _user: IqUserService = inject(IqUserService);

    constructor() {}

    async getVariable(flag: string, variable: string): Promise<unknown> {
        const user: IIqUser = this._user.loggedInUser()();
        const client: Client | null = await this.getClient();
        const userContext: OptimizelyUserContext | null | undefined =
            user?.loggedIn
                ? client?.createUserContext(user.id, user)
                : client?.createUserContext('user_id', {});

        if (!userContext) return null;

        const decision: OptimizelyDecision = userContext.decide(flag);

        return decision.variables[variable] as IqOptimizelyVariable;
    }

    async getClient(): Promise<Client | null> {
        /**
         * Not sure if this datafile is needed or not. I am going to keep it
         * to be consistent with the current feature flag, but we might remove it
         * so we aren't calling it everytime we leverage the client. (@georgepe_dat)
         */
        const dataFile = await fetch(this._config.dataFileUrl, {
            mode: 'cors',
            cache: 'no-store',
        }).then((res: Response) => res.json());
        const clientConfig: Config = {
            datafile: dataFile,
            sdkKey: this._config.sdkKey,
            logLevel: this._config.logLevel,
        };
        const client: Client | null = createInstance(clientConfig);

        if (client) {
            const { success, reason } = await client.onReady();

            if (!success) console.error(reason);
        }

        return client;
    }
}
